/**
 * AJAX Product Filtering
 */

import {
	checkWindowSize,
	isScrollLocked,
	setLockScroll,
} from '../helpers/helpers.js';

window.kiwiGetArchiveContext = function () {
	const el = document.querySelector('.js-archive-context');
	const taxonomy = el ? el.getAttribute('data-archive-taxonomy') || '' : '';
	const termIdRaw = el ? el.getAttribute('data-archive-term-id') || '' : '';
	const termId = Number.isFinite(parseInt(termIdRaw, 10))
		? parseInt(termIdRaw, 10)
		: 0;

	return { taxonomy, termId };
};

window.kiwiShouldUseLoadingOverlay = function () {
	// Only show on mobile/tablet for UX (avoid blocking desktop interactions).
	if (typeof checkWindowSize === 'function') {
		return checkWindowSize(992);
	}
	return window.innerWidth <= 992;
};

window.kiwiShowLoadingOverlay = function () {
	const overlay = document.querySelector('.js-overlay');
	if (!overlay) {
		return;
	}

	// Track whether we turned overlay on, so we can revert safely.
	if (!overlay.classList.contains('is-active')) {
		overlay.dataset.kiwiLoadingActivated = '1';
		overlay.classList.add('is-active');
	}

	overlay.classList.add('is-loading');

	const locked = typeof isScrollLocked === 'function' ? isScrollLocked() : false;
	if (!locked) {
		const html = document.documentElement;
		if (html) {
			html.dataset.kiwiLoadingLocked = '1';
		}
		if (typeof setLockScroll === 'function') {
			setLockScroll(true);
		} else if (html) {
			html.classList.add('locked');
		}
	}
};

window.kiwiHideLoadingOverlay = function () {
	const overlay = document.querySelector('.js-overlay');
	if (overlay) {
		overlay.classList.remove('is-loading');

		if (overlay.dataset.kiwiLoadingActivated === '1') {
			overlay.classList.remove('is-active');
			delete overlay.dataset.kiwiLoadingActivated;
		}
	}

	const html = document.documentElement;
	if (html && html.dataset.kiwiLoadingLocked === '1') {
		if (typeof setLockScroll === 'function') {
			setLockScroll(false);
		} else {
			html.classList.remove('locked');
		}
		delete html.dataset.kiwiLoadingLocked;
	}
};

window.kiwiFilterProducts = function (attribute, values) {
	// Prevent race conditions: only the latest request may update the UI.
	window.__kiwiFilterRequestSeq = (window.__kiwiFilterRequestSeq || 0) + 1;
	const requestSeq = window.__kiwiFilterRequestSeq;

	if (
		typeof window.kiwiShouldUseLoadingOverlay === 'function' &&
		window.kiwiShouldUseLoadingOverlay()
	) {
		window.kiwiShowLoadingOverlay();
	}

	const safeValues = Array.isArray(values) ? values : [];
	const hasAttribute = typeof attribute === 'string' && attribute.length > 0;

	// Get current filters from URL
	const urlParams = new URLSearchParams(window.location.search);
	const currentFilters = {};

	// Collect all filter_ parameters
	for (const [key, value] of urlParams.entries()) {
		if (key.startsWith('filter_')) {
			const attr = key.replace('filter_', '');
			currentFilters[attr] = value.split(',');
		}
	}

	// Update the specific attribute filter (when called for a specific attribute)
	if (hasAttribute) {
		if (safeValues.length > 0) {
			currentFilters[attribute] = safeValues;
		} else {
			delete currentFilters[attribute];
		}
	}

	// Get category ID if on category page
	const categoryElement = document.querySelector('[data-category]');
	const categoryId = categoryElement
		? categoryElement.getAttribute('data-category')
		: 0;

	// Get current archive context (category/brand/tag)
	const archive =
		typeof window.kiwiGetArchiveContext === 'function'
			? window.kiwiGetArchiveContext()
			: { taxonomy: '', termId: 0 };
	const archiveTaxonomy = archive.taxonomy;
	const archiveTermId = archive.termId;

	// Get current orderby
	const productListContainer = document.querySelector('.product-list-container');
	const skeleton = document.querySelector('.js-product-list__skeleton');
	const existingPagination = document.querySelector('.woocommerce-pagination');

	if (productListContainer) {
		productListContainer.style.display = 'none';
	}
	if (skeleton) {
		skeleton.style.display = 'block';
	}
	if (existingPagination) {
		existingPagination.style.display = 'none';
	}

	// Perform AJAX request
	const formData = new FormData();
	formData.append('action', 'kiwi_filter_products');
	formData.append('nonce', kiwi_ajax.nonce);
	formData.append('filters', JSON.stringify(currentFilters));
	formData.append('category_id', categoryId);
	formData.append('archive_taxonomy', archiveTaxonomy);
	formData.append('archive_term_id', archiveTermId);

	// Support product search pages (preserve "s" query)
	const searchTerm = urlParams.get('s') || '';
	if (searchTerm) {
		formData.append('search_term', searchTerm);
	}
	formData.append('paged', 1);

	fetch(kiwi_ajax.ajax_url, {
		method: 'POST',
		body: formData,
	})
		.then((response) => response.json())
		.then((data) => {
			if (requestSeq !== window.__kiwiFilterRequestSeq) {
				return;
			}
			if (data.success) {
				// Hide skeleton first
				if (skeleton) {
					skeleton.style.display = 'none';
				}

				// Update and show product list
				if (productListContainer) {
					const productList = productListContainer.querySelector('.product-list');
					if (productList) {
						productList.innerHTML = data.data.products;
					}
					productListContainer.style.display = '';
				}

				// Update pagination if exists
				const paginationHtml = data.data.pagination || '';
				const paginationContainer = document.querySelector(
					'.woocommerce-pagination'
				);
				if (paginationHtml) {
					if (paginationContainer) {
						paginationContainer.outerHTML = paginationHtml;
					} else if (productListContainer) {
						productListContainer.insertAdjacentHTML('afterend', paginationHtml);
					}
				} else if (paginationContainer) {
					paginationContainer.remove();
				}

				// Ensure pagination is visible after update/insert
				const updatedPagination = document.querySelector('.woocommerce-pagination');
				if (updatedPagination) {
					updatedPagination.style.display = '';
				}

				// Update filter dropdowns UI (never block URL update if UI sync fails)
				try {
					if (typeof window.updateFilterUI === 'function') {
						window.updateFilterUI(
							currentFilters,
							(data.data && data.data.available_attributes) || {},
							(data.data && data.data.available_subcategories) || []
						);
					}
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error('updateFilterUI failed', e);
				}

				// Update URL without reloading (preserve non-filter params like s/orderby)
				const newUrl = new URL(window.location.href);
				for (const key of Array.from(newUrl.searchParams.keys())) {
					if (key.startsWith('filter_')) {
						newUrl.searchParams.delete(key);
					}
				}
				Object.keys(currentFilters).forEach((attr) => {
					if (currentFilters[attr].length > 0) {
						newUrl.searchParams.set('filter_' + attr, currentFilters[attr].join(','));
					}
				});

				window.history.pushState({}, '', newUrl.toString());

				// Ensure global clear button state stays correct
				if (typeof window.updateClearFiltersButton === 'function') {
					window.updateClearFiltersButton(currentFilters);
				}

				// Scroll to products
				const shopTopContainer = document.querySelector('.shop-top-container');
				if (shopTopContainer) {
					shopTopContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}

			if (typeof window.kiwiHideLoadingOverlay === 'function') {
				window.kiwiHideLoadingOverlay();
			}
		})
		.catch(() => {
			if (requestSeq !== window.__kiwiFilterRequestSeq) {
				return;
			}
			if (skeleton) {
				skeleton.style.display = 'none';
			}
			if (productListContainer) {
				productListContainer.style.display = '';
			}
			if (existingPagination) {
				existingPagination.style.display = '';
			}

			if (typeof window.kiwiHideLoadingOverlay === 'function') {
				window.kiwiHideLoadingOverlay();
			}
		});
};

/**
 * Update filter UI after AJAX.
 *
 * @param {Record<string, string[]>} currentFilters         Active filters keyed by attribute.
 * @param {Record<string, string[]>} availableAttributes    Available term slugs keyed by attribute.
 * @param {number[]}                 availableSubcategories Available subcategory term IDs.
 */
window.updateFilterUI = function (
	currentFilters,
	availableAttributes,
	availableSubcategories
) {
	// Update attribute filter dropdowns
	document
		.querySelectorAll('.woocommerce-attribute-filter')
		.forEach((widget) => {
			const attribute = widget.getAttribute('data-attribute');
			const toggleBtn = widget.querySelector('.attribute-filter-toggle');
			if (!attribute || !toggleBtn) {
				return;
			}
			const hasFilters =
				currentFilters[attribute] && currentFilters[attribute].length > 0;

			// Update checkboxes based on current filters
			const checkboxes = widget.querySelectorAll('input[type="checkbox"]');
			checkboxes.forEach((checkbox) => {
				const isSelected =
					hasFilters && currentFilters[attribute].includes(checkbox.value);
				checkbox.checked = isSelected;
				const option = checkbox.closest('.attribute-option');
				if (option) {
					if (isSelected) {
						option.classList.add('is-active');
					} else {
						option.classList.remove('is-active');
					}
				}
			});

			// Enable/disable apply button based on current selections
			const applyBtn = widget.querySelector('.attribute-apply-filter');
			if (applyBtn) {
				const anyChecked =
					widget.querySelectorAll('input[type="checkbox"]:checked').length > 0;
				applyBtn.disabled = !anyChecked;
				if (anyChecked) {
					applyBtn.removeAttribute('disabled');
				} else {
					applyBtn.setAttribute('disabled', 'disabled');
				}
			}

			// Show/hide clear icon
			if (hasFilters) {
				toggleBtn.classList.add('has-clear');
			} else if (!hasFilters) {
				toggleBtn.classList.remove('has-clear');
			}

			// Check if we have any active filters at all
			const hasAnyFilters = Object.keys(currentFilters).length > 0;

			// Disable/enable options based on availability
			const options = widget.querySelectorAll('.attribute-option');
			options.forEach((option) => {
				const input = option.querySelector('input');
				if (!input) {
					return;
				}
				const value = input.value;

				// If no filters are active OR this filter is active, enable all options
				if (!hasAnyFilters || hasFilters) {
					option.style.opacity = '';
					option.style.pointerEvents = '';
				} else if (availableAttributes[attribute]) {
					// If other filters are active, only enable available options
					const isAvailable = availableAttributes[attribute].includes(value);

					if (!isAvailable && !input.checked) {
						option.style.opacity = '0.5';
						option.style.pointerEvents = 'none';
					} else {
						option.style.opacity = '';
						option.style.pointerEvents = '';
					}
				}
			});
		});

	// Update subcategory filter
	const subcategoryWidget = document.querySelector(
		'.woocommerce-subcategory-filter'
	);
	if (subcategoryWidget) {
		// Check if we have any active filters
		const hasAnyFilters = Object.keys(currentFilters).length > 0;

		const options = subcategoryWidget.querySelectorAll('.subcategory-option');
		options.forEach((option) => {
			const input = option.querySelector('input');
			if (!input) {
				return;
			}
			const termId = parseInt(input.getAttribute('data-term-id') || input.value);

			// If no filters are active, enable all subcategories
			if (!hasAnyFilters) {
				option.style.opacity = '';
				option.style.pointerEvents = '';
			} else if (Array.isArray(availableSubcategories)) {
				// If filters are active, only enable available subcategories
				const isAvailable = availableSubcategories.includes(termId);

				if (!isAvailable && !input.checked) {
					option.style.opacity = '0.5';
					option.style.pointerEvents = 'none';
				} else {
					option.style.opacity = '';
					option.style.pointerEvents = '';
				}
			}
		});
	}

	// Update mobile footer filter (popup)
	const mobileFilterPopup = document.querySelector('.js-footer-mobile-filter');
	if (mobileFilterPopup) {
		const mapNameToFilterKey = function (name) {
			if (!name) {
				return '';
			}
			if (name === 'product_brand') {
				return 'brand';
			}
			if (name.startsWith('pa_')) {
				return name.replace(/^pa_/, '');
			}
			if (name.startsWith('filter_')) {
				return name.replace(/^filter_/, '');
			}
			return name;
		};

		const normalizePathname = function (url) {
			try {
				const u = new URL(url, window.location.origin);
				return u.pathname.replace(/\/page\/\d+\/?$/i, '/').replace(/\/+$/g, '/');
			} catch (_e) {
				return '';
			}
		};

		// Mark active subcategory link (mobile)
		const currentPath = normalizePathname(window.location.href);
		mobileFilterPopup.querySelectorAll('a.product-cat-filter').forEach((link) => {
			const linkPath = normalizePathname(link.getAttribute('href') || '');
			if (linkPath && currentPath && linkPath === currentPath) {
				link.classList.add('is-active');
			} else {
				link.classList.remove('is-active');
			}
		});

		// Disable/enable checkbox options based on availability
		const hasAnyFilters =
			currentFilters && Object.keys(currentFilters).length > 0;
		mobileFilterPopup
			.querySelectorAll('input.js-filter-checkbox[type="checkbox"]')
			.forEach((input) => {
				const filterKey = mapNameToFilterKey(input.getAttribute('name') || '');
				if (!filterKey) {
					return;
				}

				const groupHasFilters =
					currentFilters &&
					Array.isArray(currentFilters[filterKey]) &&
					currentFilters[filterKey].length > 0;

				const label = input.closest('label');
				const resetOption = function () {
					input.disabled = false;
					input.removeAttribute('disabled');
					if (label) {
						label.style.opacity = '';
						label.style.pointerEvents = '';
					}
				};

				const disableOption = function () {
					input.disabled = true;
					input.setAttribute('disabled', 'disabled');
					if (label) {
						label.style.opacity = '0.5';
						label.style.pointerEvents = 'none';
					}
				};

				// If no filters are active OR this group is active, enable all options.
				if (!hasAnyFilters || groupHasFilters) {
					resetOption();
					return;
				}

				if (availableAttributes && Array.isArray(availableAttributes[filterKey])) {
					const isAvailable = availableAttributes[filterKey].includes(input.value);
					if (!isAvailable && !input.checked) {
						disableOption();
					} else {
						resetOption();
					}
				} else {
					resetOption();
				}
			});
	}

	if (typeof window.updateClearFiltersButton === 'function') {
		window.updateClearFiltersButton(currentFilters);
	}
};

/**
 * Toggle global "Clear filters" button state.
 *
 * @param {Record<string, string[]>} currentFilters Active filters keyed by attribute.
 */
window.updateClearFiltersButton = function (currentFilters) {
	const clearFiltersWrap = document.querySelector('.js-clear-filters');
	const clearFilterButtons = document.querySelectorAll('.js-clear-filters-btn');
	if (!clearFiltersWrap && clearFilterButtons.length === 0) {
		return;
	}

	const hasAttributeFilters =
		currentFilters && Object.keys(currentFilters).length > 0;
	const hasCategoryFilter = !!document.querySelector(
		'.woocommerce-subcategory-filter .subcategory-filter-toggle[data-parent-url]'
	);
	const hasAnyFilters = hasAttributeFilters || hasCategoryFilter;

	if (clearFiltersWrap) {
		clearFiltersWrap.style.display = hasAnyFilters ? 'block' : 'none';
	}

	clearFilterButtons.forEach((btn) => {
		btn.disabled = !hasAnyFilters;
		if (hasAnyFilters) {
			btn.removeAttribute('disabled');
		} else {
			btn.setAttribute('disabled', 'disabled');
		}

		// Mobile footer button uses inline display:none in markup; keep it in sync.
		const isMobileFooterBtn = !!btn.closest('.footer-mobile');
		const isDesktopBtn = !!btn.closest('.js-clear-filters');
		if (isMobileFooterBtn && !isDesktopBtn) {
			btn.style.display = hasAnyFilters ? '' : 'none';
		}
	});
};

/**
 * Clear all filter_* URL params, reset UI, and re-fetch products.
 */
window.kiwiClearAllFilters = function () {
	const stripPageSegment = function (pathname) {
		// Remove WooCommerce pagination segment like /page/2/
		return pathname.replace(/\/page\/\d+\/?$/i, '/');
	};

	// If we are on a subcategory page, the category widget exposes the parent URL.
	// Clearing filters should also clear the category filter by navigating to the parent.
	const subcategoryToggle = document.querySelector(
		'.woocommerce-subcategory-filter .subcategory-filter-toggle'
	);
	const parentUrl = subcategoryToggle ? subcategoryToggle.dataset.parentUrl : '';
	if (parentUrl) {
		if (
			typeof window.kiwiShouldUseLoadingOverlay === 'function' &&
			window.kiwiShouldUseLoadingOverlay() &&
			typeof window.kiwiShowLoadingOverlay === 'function'
		) {
			window.kiwiShowLoadingOverlay();
		}

		const currentUrl = new URL(window.location.href);
		let targetUrl;
		try {
			targetUrl = new URL(parentUrl, window.location.origin);
		} catch (_e) {
			targetUrl = null;
		}

		if (targetUrl) {
			targetUrl.pathname = stripPageSegment(targetUrl.pathname);

			// Preserve any non-filter query params (e.g. orderby), but drop filter_*.
			for (const [key, value] of currentUrl.searchParams.entries()) {
				if (!key.startsWith('filter_')) {
					targetUrl.searchParams.set(key, value);
				}
			}

			window.location.href = targetUrl.toString();
			return;
		}
	}

	const newUrl = new URL(window.location.href);
	newUrl.pathname = stripPageSegment(newUrl.pathname);

	// Remove all filter_* params
	for (const key of Array.from(newUrl.searchParams.keys())) {
		if (key.startsWith('filter_')) {
			newUrl.searchParams.delete(key);
		}
	}

	if (
		typeof window.kiwiShouldUseLoadingOverlay === 'function' &&
		window.kiwiShouldUseLoadingOverlay() &&
		typeof window.kiwiShowLoadingOverlay === 'function'
	) {
		window.kiwiShowLoadingOverlay();
	}

	// Hard navigate to avoid stale in-memory filter state after reset.
	window.location.href = newUrl.toString();
};

/**
 * Re-apply disabled filter options on full page loads (refresh/pagination)
 */
document.addEventListener('DOMContentLoaded', function () {
	if (typeof window.kiwiInitFilterWidgets === 'function') {
		window.kiwiInitFilterWidgets();
	}

	// Mobile footer filter (popup) integration
	const mobileFilterPopup = document.querySelector('.js-footer-mobile-filter');
	if (mobileFilterPopup) {
		// Show loader on category link redirects
		if (!mobileFilterPopup.dataset.kiwiCatLinksBound) {
			mobileFilterPopup.dataset.kiwiCatLinksBound = '1';
			mobileFilterPopup
				.querySelectorAll('a.product-cat-filter')
				.forEach((link) => {
					link.addEventListener('click', function () {
						if (
							typeof window.kiwiShouldUseLoadingOverlay === 'function' &&
							window.kiwiShouldUseLoadingOverlay() &&
							typeof window.kiwiShowLoadingOverlay === 'function'
						) {
							window.kiwiShowLoadingOverlay();
						}
					});
				});
		}

		const mapNameToFilterKey = function (name) {
			if (!name) {
				return '';
			}
			if (name === 'product_brand') {
				return 'brand';
			}
			if (name.startsWith('pa_')) {
				return name.replace(/^pa_/, '');
			}
			if (name.startsWith('filter_')) {
				return name.replace(/^filter_/, '');
			}
			return name;
		};

		// Sync checked states from URL filter_* params
		const urlParams = new URLSearchParams(window.location.search);
		const currentFilters = {};
		for (const [key, value] of urlParams.entries()) {
			if (key.startsWith('filter_')) {
				const attr = key.replace('filter_', '');
				currentFilters[attr] = value.split(',');
			}
		}

		mobileFilterPopup
			.querySelectorAll('input.js-filter-checkbox[type="checkbox"]')
			.forEach((input) => {
				const filterKey = mapNameToFilterKey(input.getAttribute('name') || '');
				if (!filterKey) {
					return;
				}
				const selected = currentFilters[filterKey] || [];
				input.checked = selected.includes(input.value);
			});

		const submitBtn = mobileFilterPopup.querySelector('.js-mobile-filter-submit');
		if (submitBtn && !submitBtn.dataset.kiwiBound) {
			submitBtn.dataset.kiwiBound = '1';
			submitBtn.addEventListener('click', function (e) {
				e.preventDefault();

				// Collect filters from mobile popup checkboxes
				const selectedByKey = {};
				mobileFilterPopup
					.querySelectorAll('input.js-filter-checkbox[type="checkbox"]:checked')
					.forEach((input) => {
						const filterKey = mapNameToFilterKey(input.getAttribute('name') || '');
						if (!filterKey) {
							return;
						}
						if (!Array.isArray(selectedByKey[filterKey])) {
							selectedByKey[filterKey] = [];
						}
						selectedByKey[filterKey].push(input.value);
					});

				// Update URL (preserve non-filter params like s/orderby)
				const newUrl = new URL(window.location.href);
				for (const key of Array.from(newUrl.searchParams.keys())) {
					if (key.startsWith('filter_')) {
						newUrl.searchParams.delete(key);
					}
				}
				Object.keys(selectedByKey).forEach((filterKey) => {
					if (selectedByKey[filterKey].length > 0) {
						newUrl.searchParams.set(
							'filter_' + filterKey,
							selectedByKey[filterKey].join(',')
						);
					}
				});
				window.history.replaceState({}, '', newUrl);

				// Update global clear button state immediately
				if (typeof window.updateClearFiltersButton === 'function') {
					window.updateClearFiltersButton(selectedByKey);
				}

				// Close popup (simulate overlay click)
				const overlay = document.querySelector('.js-overlay');
				if (overlay) {
					overlay.click();
				}

				// Re-fetch products based on URL filters
				if (typeof window.kiwiFilterProducts === 'function') {
					window.kiwiFilterProducts();
				}
			});
		}
	}

	// Wire global clear-filters button
	document
		.querySelectorAll('.js-clear-filters-btn')
		.forEach((clearFiltersBtn) => {
			if (!clearFiltersBtn || clearFiltersBtn.dataset.kiwiBound) {
				return;
			}
			clearFiltersBtn.dataset.kiwiBound = '1';
			clearFiltersBtn.addEventListener('click', function (e) {
				e.preventDefault();
				window.kiwiClearAllFilters();
			});
		});

	const urlParams = new URLSearchParams(window.location.search);
	const currentFilters = {};

	for (const [key, value] of urlParams.entries()) {
		if (key.startsWith('filter_')) {
			const attr = key.replace('filter_', '');
			currentFilters[attr] = value.split(',');
		}
	}

	// Always update global clear button state on page load
	if (typeof window.updateClearFiltersButton === 'function') {
		window.updateClearFiltersButton(currentFilters);
	}

	if (Object.keys(currentFilters).length === 0) {
		return;
	}

	// Sync checkbox states & per-filter clear icons immediately (without disabling)
	window.updateFilterUI(currentFilters, {}, null);

	const categoryElement = document.querySelector('[data-category]');
	const categoryId = categoryElement
		? categoryElement.getAttribute('data-category')
		: 0;

	const archive =
		typeof window.kiwiGetArchiveContext === 'function'
			? window.kiwiGetArchiveContext()
			: { taxonomy: '', termId: 0 };

	const formData = new FormData();
	formData.append('action', 'kiwi_get_filter_availability');
	formData.append('nonce', kiwi_ajax.nonce);
	formData.append('filters', JSON.stringify(currentFilters));
	formData.append('category_id', categoryId);
	formData.append('archive_taxonomy', archive.taxonomy);
	formData.append('archive_term_id', archive.termId);

	const searchTerm = urlParams.get('s') || '';
	if (searchTerm) {
		formData.append('search_term', searchTerm);
	}

	fetch(kiwi_ajax.ajax_url, {
		method: 'POST',
		body: formData,
	})
		.then((response) => response.json())
		.then((data) => {
			if (!data || !data.success || !data.data) {
				return;
			}

			window.updateFilterUI(
				currentFilters,
				data.data.available_attributes || {},
				data.data.available_subcategories || []
			);
		})
		.catch(() => {
			// no-op
		});
});

/**
 * Close all filter dropdowns
 */
window.closeAllDropdowns = function () {
	document
		.querySelectorAll('.subcategory-dropdown, .attribute-dropdown')
		.forEach((dropdown) => {
			dropdown.style.display = 'none';
			const toggle = dropdown.previousElementSibling;
			if (toggle) {
				toggle.setAttribute('aria-expanded', 'false');
			}
		});
};

/**
 * Bind desktop filter widget interactions (attribute + category widgets).
 * Replaces the inline <script> blocks inside widget PHP.
 */
window.kiwiInitFilterWidgets = function () {
	// Attribute + brand widgets
	document
		.querySelectorAll('.woocommerce-attribute-filter')
		.forEach((widget) => {
			if (widget.dataset.kiwiBound === '1') {
				return;
			}
			widget.dataset.kiwiBound = '1';

			const attribute = widget.getAttribute('data-attribute') || '';
			const toggleBtn = widget.querySelector('.attribute-filter-toggle');
			const dropdown = widget.querySelector('.attribute-dropdown');
			const applyBtn = widget.querySelector('.attribute-apply-filter');
			if (!attribute || !toggleBtn || !dropdown) {
				return;
			}

			const syncApplyBtn = function () {
				if (!applyBtn) {
					return;
				}
				const anyChecked =
					widget.querySelectorAll('input[type="checkbox"]:checked').length > 0;
				applyBtn.disabled = !anyChecked;
				if (anyChecked) {
					applyBtn.removeAttribute('disabled');
				} else {
					applyBtn.setAttribute('disabled', 'disabled');
				}
			};

			// Toggle dropdown (ignore clear icon clicks)
			toggleBtn.addEventListener('click', function (e) {
				if (e.target && e.target.closest && e.target.closest('.clear-icon')) {
					return;
				}
				e.stopPropagation();
				const isOpen = dropdown.style.display === 'block';
				if (window.closeAllDropdowns) {
					window.closeAllDropdowns();
				}
				dropdown.style.display = isOpen ? 'none' : 'block';
				toggleBtn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
			});

			// Clear filter (click on clear icon inside toggle button)
			const clearIcon = toggleBtn.querySelector('.clear-icon');
			if (clearIcon) {
				clearIcon.addEventListener('click', function (e) {
					e.preventDefault();
					e.stopPropagation();
					if (typeof window.kiwiFilterProducts === 'function') {
						window.kiwiFilterProducts(attribute, []);
					}
				});
			}

			// Apply filter
			if (applyBtn) {
				applyBtn.addEventListener('click', function (e) {
					e.preventDefault();
					if (applyBtn.disabled) {
						return;
					}
					const selectedValues = Array.from(
						widget.querySelectorAll('input[type="checkbox"]:checked')
					).map((cb) => cb.value);

					dropdown.style.display = 'none';
					toggleBtn.setAttribute('aria-expanded', 'false');

					if (typeof window.kiwiFilterProducts === 'function') {
						window.kiwiFilterProducts(attribute, selectedValues);
					}
				});
			}

			widget.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
				cb.addEventListener('change', syncApplyBtn);
			});
			syncApplyBtn();
		});

	// Category (subcategory) widget
	document
		.querySelectorAll('.woocommerce-subcategory-filter')
		.forEach((wrap) => {
			if (wrap.dataset.kiwiBound === '1') {
				return;
			}
			wrap.dataset.kiwiBound = '1';

			const toggleBtn = wrap.querySelector('.subcategory-filter-toggle');
			const dropdown = wrap.querySelector('.subcategory-dropdown');
			if (!toggleBtn || !dropdown) {
				return;
			}

			const clearIcon = toggleBtn.querySelector('.clear-icon');
			if (clearIcon && toggleBtn.dataset.parentUrl) {
				clearIcon.addEventListener('click', function (e) {
					e.preventDefault();
					e.stopPropagation();
					window.location.href = toggleBtn.dataset.parentUrl;
				});
			}

			toggleBtn.addEventListener('click', function (e) {
				e.stopPropagation();
				const isOpen = dropdown.style.display === 'block';
				if (!isOpen && window.closeAllDropdowns) {
					window.closeAllDropdowns();
				}
				dropdown.style.display = isOpen ? 'none' : 'block';
				toggleBtn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
			});

			dropdown.querySelectorAll('input[type="radio"]').forEach((radio) => {
				radio.addEventListener('change', function () {
					if (this.checked && this.dataset.url) {
						dropdown.style.display = 'none';
						toggleBtn.setAttribute('aria-expanded', 'false');
						window.location.href = this.dataset.url;
					}
				});
			});
		});

	// Global outside click closes all dropdowns
	if (!document.body.dataset.kiwiFiltersOutsideBound) {
		document.body.dataset.kiwiFiltersOutsideBound = '1';
		document.addEventListener('click', function (e) {
			const inAttribute =
				e.target && e.target.closest
					? e.target.closest('.woocommerce-attribute-filter')
					: null;
			const inCategory =
				e.target && e.target.closest
					? e.target.closest('.woocommerce-subcategory-filter')
					: null;
			if (!inAttribute && !inCategory) {
				if (typeof window.closeAllDropdowns === 'function') {
					window.closeAllDropdowns();
				}
			}
		});
	}
};
